/**
 * Error Step Component
 * Displays error state with retry options.
 * Restyled with NowRamp theme tokens.
 */

import { useRamp } from '../context/RampContext';
import { useT } from '../i18n';

export interface ErrorStepProps {
  className?: string;
  onRetry?: () => void;
  onDone?: () => void;
  doneButtonText?: string;
  supportEmail?: string;
}

export function ErrorStep({
  className = '',
  onRetry,
  onDone,
  doneButtonText,
  supportEmail = 'support@nowramp.com',
}: ErrorStepProps) {
  const { state, orderStatus, dispatch, goToStep } = useRamp();
  const t = useT();
  const isSell = state.flowType === 'sell';

  const getErrorTitle = () => {
    if (orderStatus) {
      switch (orderStatus.status) {
        case 'failed': return t('errorStep.failed');
        case 'cancelled': return t('errorStep.cancelled');
        case 'expired': return t('errorStep.expired');
        case 'refunded': return t('errorStep.refunded');
        default: return t('errorStep.unknown');
      }
    }
    return t('errorStep.unknown');
  };

  const getErrorMessage = () => {
    if (state.errorMessage) return state.errorMessage;
    if (orderStatus) {
      switch (orderStatus.status) {
        case 'failed':
          return isSell
            ? t('errorStep.failedSell')
            : t('errorStep.failedBuy');
        case 'cancelled':
          return isSell
            ? t('errorStep.cancelledSell')
            : t('errorStep.cancelledBuy');
        case 'expired':
          return isSell
            ? t('errorStep.expiredSell')
            : t('errorStep.expiredBuy');
        case 'refunded':
          return isSell
            ? t('errorStep.refundedSell')
            : t('errorStep.refundedBuy');
        default:
          return t('errorStep.genericDesc');
      }
    }
    return t('errorStep.genericDesc');
  };

  const handleRetry = () => {
    dispatch({ type: 'CLEAR_ERROR' });
    if (onRetry) {
      onRetry();
    } else {
      dispatch({ type: 'RESET' });
      goToStep('amount');
    }
  };

  const handleStartOver = () => {
    dispatch({ type: 'RESET' });
    goToStep('amount');
  };

  return (
    <div className={`flex flex-col items-center px-8 py-12 text-center ${className}`}>
      {/* Error icon */}
      <div
        className="flex items-center justify-center mb-5"
        style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--t-error, #EF4444)' }}
      >
        <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
        </svg>
      </div>

      <h2 className="text-[20px] font-semibold mb-2" style={{ color: 'var(--t-text)' }}>
        {getErrorTitle()}
      </h2>

      <p className="text-[14px] mb-6" style={{ color: 'var(--t-text-secondary)' }}>
        {getErrorMessage()}
      </p>

      {orderStatus && (
        <div className="w-full text-left mb-6" style={{ background: 'var(--t-surface)', borderRadius: 'var(--t-card-radius, 20px)', padding: 16 }}>
          <div className="flex justify-between items-center py-2">
            <span className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>{t('errorStep.orderId')}</span>
            <span className="text-[13px] font-medium font-mono" style={{ color: 'var(--t-text)' }}>
              {orderStatus.orderId}
            </span>
          </div>
        </div>
      )}

      <div className="w-full flex flex-col gap-2">
        <button
          className="w-full font-semibold text-[16px] transition-all"
          style={{ height: 60, borderRadius: 'var(--t-cta-radius, 100px)', background: 'var(--t-cta-bg)', color: 'var(--t-cta-text)' }}
          onClick={handleRetry}
        >
          {t('errorStep.tryAgain')}
        </button>
        <button
          className="w-full font-semibold text-[14px] transition-all"
          style={{ height: 48, borderRadius: 'var(--t-cta-radius, 100px)', background: 'var(--t-surface)', color: 'var(--t-text)' }}
          onClick={handleStartOver}
        >
          {t('errorStep.startOver')}
        </button>
        {onDone && (
          <button
            className="w-full font-semibold text-[14px] transition-all mt-1"
            style={{ height: 48, borderRadius: 'var(--t-cta-radius, 100px)', background: 'transparent',
                     color: 'var(--t-text-secondary)', border: '1px solid var(--t-divider)' }}
            onClick={onDone}
            aria-label={doneButtonText || t('errorStep.returnToMerchant')}
          >
            {doneButtonText || t('errorStep.return')}
          </button>
        )}
      </div>

      <div className="mt-6">
        <p className="text-[13px]" style={{ color: 'var(--t-text-secondary)' }}>
          {t('errorStep.needHelp')}{' '}
          <a
            href={`mailto:${supportEmail}`}
            className="underline"
            style={{ color: 'var(--t-accent)' }}
          >
            {supportEmail}
          </a>
        </p>
        {orderStatus && (
          <p className="text-[12px] mt-1" style={{ color: 'var(--t-text-muted)' }}>
            {t('errorStep.reference', { orderId: orderStatus.orderId })}
          </p>
        )}
      </div>
    </div>
  );
}
